/**
 * MER-503: tick-based failure backoff for background probes (the
 * 60-second flag poll, and any future interval probe). A probe that
 * fails should not be retried at full cadence against a degraded
 * backend: consecutive failures double the number of ticks skipped
 * between attempts, capped, and one success resets to full cadence.
 *
 * Deliberately tick-based rather than wall-clock so the caller's
 * existing setInterval stays the single source of timing and tests
 * need no clock at all. Plain .mjs so the node:test suite imports it
 * directly (the telemetryQueue.mjs precedent).
 */

/**
 * @param {{ maxSkipTicks?: number }} [opts]   cap on ticks skipped
 *   between attempts after repeated failures (default 8: with a 60s
 *   interval, a persistently failing probe settles at one attempt
 *   every ~8 minutes).
 */
export function createProbeBackoff(opts = {}) {
  const maxSkipTicks = opts.maxSkipTicks ?? 8;
  let consecutiveFailures = 0;
  let ticksUntilAttempt = 0;

  return {
    /**
     * Call once per interval tick. True = attempt the probe now;
     * false = skip this tick (still backing off).
     */
    shouldAttempt() {
      if (ticksUntilAttempt > 0) {
        ticksUntilAttempt--;
        return false;
      }
      return true;
    },

    onSuccess() {
      consecutiveFailures = 0;
      ticksUntilAttempt = 0;
    },

    onFailure() {
      consecutiveFailures++;
      ticksUntilAttempt = Math.min(2 ** (consecutiveFailures - 1), maxSkipTicks);
    },

    failures() {
      return consecutiveFailures;
    },
  };
}
